<template>
    <Select
        v-bind="$attrs"
        label-field="name"
        :placeholder="placeholder || $t('registration.institution.placeholder')"
        :options="institutions"
        :loading="institutions.length === 0"
        truncate
        @input="$emit('input', $event)"
    >
        <template #options-footer>
            <div class="flex flex-col items-center px-4 py-3">
                <div>{{ $t('registration.institution.missingTitle') }}</div>
                <BtnCta
                    variant="tertiary"
                    class="!font-normal !text-sm"
                    :label="$t('registration.institution.missingCta')"
                    compact
                    @click="openWaitingListModal"
                />
            </div>
        </template>
    </Select>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent } from 'vue';
import { getInstitutions } from '@/api/gateway/legacy-api/university';
import { InstitutionType } from '@/api/gateway/legacy-api/resources/UniversityResource';
import type { Institution } from './types';

/**
 * Extends SearchMixin
 */
export default defineComponent({
    name: 'SelectInstitution',
    inheritAttrs: false,
    i18nOptions: {
        namespaces: ['authentication'],
    },
    props: {
        /**
         * The placeholder.
         */
        placeholder: {
            type: String,
            default: undefined,
        },
        /**
         * To not include "Higher Education" option into the dropdown.
         */
        withoutHigherEducationOption: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'input',
    ],
    setup() {
        return {
            InstitutionType,
        };
    },
    data() {
        return {
            institutions: [] as Institution[],
        };
    },
    async created() {
        this.institutions = await this.getInstitutions();
    },
    methods: {
        async getInstitutions() {
            const response = await getInstitutions();

            const data = response?.data || [];
            const mappedResponse = [] as Institution[];
            if (data.universities?.results?.length) {
                data?.universities?.results.forEach((institution) => {
                    mappedResponse.push({
                        id: institution.id,
                        name: institution.name,
                        institution_type: InstitutionType.SCHOOL,
                    });
                });
                if (!this.withoutHigherEducationOption) {
                    const higherEducation = {
                        id: 0,
                        name: this.$t('registration.institution.higherEducation'),
                        institution_type: this.InstitutionType.UNIVERSITY,
                    };
                    mappedResponse.unshift(higherEducation);
                }
            }

            return mappedResponse;
        },
        openWaitingListModal(): void {
            this.$modal.open(
                defineAsyncComponent(() => import('@/components/ModalAddSchoolWaitingList')),
                {
                    userEmail: this.$store.getters['user/user']?.email,
                },
            );
        },
    },
});
</script>
